import React, { useState } from "react";
import {
  Card,
  Typography,
  Table,
  Space,
  DatePicker,
  Button,
  Statistic,
  Row,
  Col,
  Input,
  Spin,
  Alert,
  Select,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { motion } from "framer-motion";
import {
  useReportsServiceDownloadAccountingTransactionsReport,
  useReportsServiceGetAccountingTransactionsReport,
} from "api/queries";

const { Title } = Typography;

const PERIOD_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "This Week", value: "This Week" },
  { label: "This Month", value: "This Month" },
  { label: "This Year", value: "This Year" },
];

const DailyMonthlyTransactionsReport: React.FC = () => {
  interface KPIs {
    totalTransactions?: number;
    totalAmount?: number;
  }
  const [startDate, setStartDate] = useState(dayjs().subtract(1, "year"));
  const [endDate, setEndDate] = useState(dayjs());
  const [periodFilter, setPeriodFilter] = useState("Custom");
  const [search, setSearch] = useState("");

  const { data, isLoading, isError, error, refetch } =
    useReportsServiceGetAccountingTransactionsReport({
      startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      search,
    });

  const { mutate: downloadReport, isPending } =
    useReportsServiceDownloadAccountingTransactionsReport();

  // Period filter logic
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();
    switch (value) {
      case "Today":
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
        break;
      case "This Week":
        setStartDate(now.startOf("week"));
        setEndDate(now.endOf("week"));
        break;
      case "This Month":
        setStartDate(now.startOf("month"));
        setEndDate(now.endOf("month"));
        break;
      case "This Year":
        setStartDate(now.startOf("year"));
        setEndDate(now.endOf("year"));
        break;
      default:
        // Custom
        break;
    }
    refetch();
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date || dayjs());
    setPeriodFilter("Custom");
    refetch();
  };
  const handleEndDateChange = (date: any) => {
    setEndDate(date || dayjs());
    setPeriodFilter("Custom");
    refetch();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    refetch();
  };

  const handleDownload = () => {
    downloadReport(
      {
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        search,
      },
      {
        onSuccess: (response: { downloadUrl?: string }) => {
          if (response?.downloadUrl) {
            const link = document.createElement("a");
            link.href = response.downloadUrl;
            link.setAttribute(
              "download",
              "daily_monthly_transactions_report.xlsx"
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
      }
    );
  };

  const kpis: KPIs = data?.kpis || {};
  const tableData = (data?.items || []).map((item: any) => ({
    ...item,
    key: item.transactionId,
  }));

  const columns = [
    {
      title: "Date",
      dataIndex: "transactionDate",
      key: "transactionDate",
      render: (date: string) => dayjs(date).format("YYYY-MM-DD"),
    },
    {
      title: "Type",
      dataIndex: "transactionType",
      key: "transactionType",
      render: (type: string | null) => type || "-",
    },
    {
      title: "Description",
      dataIndex: "transactionDescription",
      key: "transactionDescription",
      render: (desc: string | null) => desc || "-",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (value: number) => `${value?.toLocaleString?.() ?? value}`,
    },
    {
      title: "Debit Account",
      dataIndex: "debitAccountName",
      key: "debitAccountName",
      render: (name: string | null) => name || "-",
    },
    {
      title: "Credit Account",
      dataIndex: "creditAccountName",
      key: "creditAccountName",
      render: (name: string | null) => name || "-",
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      render: (notes: string | null) => notes || "-",
    },
  ];

  // Type guard for error
  const getErrorMessage = (err: unknown) => {
    if (typeof err === "string") return err;
    if (err && typeof err === "object" && "message" in err)
      return (err as any).message;
    return "Unknown error";
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2 }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>Daily/Monthly Transactions Report</Title>
          <Space>
            <Input.Search
              allowClear
              placeholder="Search transactions..."
              style={{ width: 260 }}
              value={search}
              onChange={handleSearchChange}
            />
            <Select
              value={periodFilter}
              onChange={handlePeriodChange}
              style={{ width: 120 }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              style={{ width: 120 }}
              placeholder="From"
            />
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              style={{ width: 120 }}
              placeholder="To"
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isPending}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </Space>
        </Space>
        <Row gutter={24} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Statistic
              title="Total Transactions"
              value={kpis?.totalTransactions ?? 0}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Total Amount"
              value={kpis?.totalAmount?.toLocaleString?.() ?? 0}
            />
          </Col>
        </Row>
        <Card>
          {isLoading ? (
            <Spin size="large" />
          ) : isError ? (
            <Alert
              type="error"
              message="Failed to load report"
              description={getErrorMessage(error)}
            />
          ) : (
            <Table
              columns={columns}
              dataSource={tableData}
              loading={isLoading}
              pagination={{ pageSize: 20 }}
              scroll={{ x: "max-content" }}
            />
          )}
        </Card>
      </Space>
    </motion.div>
  );
};

export default DailyMonthlyTransactionsReport;
